import React from 'react';
import {Box, Heading, Icon, Tabs, Text, Image, VStack, HStack, SimpleGrid} from "@chakra-ui/react";
import {LuBed} from "react-icons/lu";
import SavingsCard from "./SavingsCard";
import {FaCheckCircle} from "react-icons/fa";
import RegionSelector from "./RegionSelector";
import {houseConfigs} from "../constants";

const Main = () => {
    return (
        <Box mx={"auto"} width={["90%", "80%", "70%", "60%"]} py={10}>
            <Box mb={8}>
                <Text fontSize={{base: "2xl !important", md: "4xl !important"}} color={"black !important"} fontWeight="bold">
                    See How to Save Hundreds on Energy Bills with Solar Panels
                </Text>
                <Text fontSize={{base: "md !important", md: "xl !important"}} color={"black !important"} mt={2}>
                    Select your house size to see how much you could save on your energy bills each year when you go
                    solar.
                </Text>
            </Box>

            <Tabs.Root defaultValue={"smallHouse"} variant={"enclosed"} align={"center"} size={{base: "md", md: "lg"}}
                       pb={10}>
                <Tabs.List
                    display="flex"
                    flexDirection={{base: "column", md: "row"}}
                    alignItems="stretch"
                    justifyContent="center"
                    gap={2}
                    w={{base: "full", md: "fit-content"}}
                    mx="auto"
                    mb={4}
                >
                    {houseConfigs.map(({ value, label }) => (
                          <Tabs.Trigger
                              key={value}
                              value={value}
                              _selected={{color: "white", bg: "green.500"}}
                              fontSize={{base: "md", md: "xl"}}
                              py={6}
                              w={{base: "100%", md: "auto"}}
                              textAlign="center"
                          >
                              <Icon as={LuBed} mr={2}/>
                              {label}
                          </Tabs.Trigger>
                      ))}
                </Tabs.List>

                {houseConfigs.map(({ value, label, ...cardProps }) => (
                    <Tabs.Content key={value} value={value}>
                      <SavingsCard {...cardProps} />
                    </Tabs.Content>
                ))}
            </Tabs.Root>

            <Box
                p={6}
                as={"section"}
                borderRadius="xl"
                boxShadow="md"
                mx="auto"
                textAlign="left"
                mb={10}
            >
                <Heading as={"h2"} size={{base: "xl", md: "3xl"}} color="green.600" mb={4}>
                    Solar Energy: A Smarter Choice for European Homes
                </Heading>

                <Image
                    src={`${process.env.PUBLIC_URL}/images/panels.webp`}
                    alt="Workers installing solar panels"
                    borderRadius="md"
                    mb={6}
                    objectFit="cover"
                    width="100%"
                    maxHeight={{ base: "auto", md: "500px" }}
                    height="auto"
                />

                <VStack spacing={4} align="start">
                    <Text fontSize={{base: "md !important", md: "xl !important"}} color={"black !important"}>
                        With rising energy costs and growing climate concerns, solar power is quickly becoming the go-to
                        solution for homeowners across Europe. From the sun-drenched rooftops of the Mediterranean
                        to the cloudier climates in the north, new solar technologies are making it easier than ever to
                        generate clean, affordable electricity — no matter where you live.
                    </Text>

                    <Text fontSize={{base: "md !important", md: "xl !important"}} color={"black !important"}>
                        Many European governments are offering generous financial incentives to encourage
                        eco-friendly upgrades. When combined with the long-term savings on utility bills, these benefits
                        make switching to solar one of the most impactful investments for your home in 2025.
                    </Text>

                    <Text fontSize={{base: "md !important", md: "xl !important"}} color={"black !important"} fontWeight="semibold">
                        Here’s why 2025 is the perfect time to go solar:
                    </Text>

                    <VStack spacing={3} align="start" pl={4}>
                        <HStack align="start">
                            <Icon as={FaCheckCircle} aria-hidden="true" color="green.500" mt={1}/>
                            <Text fontSize={{base: "sm !important", md: "lg !important"}} color={"black !important"}>
                                <strong>Tax incentives & subsidies:</strong> Many EU countries offer grants or tax
                                credits to help lower the cost of installation.
                            </Text>
                        </HStack>

                        <HStack align="start">
                            <Icon as={FaCheckCircle} aria-hidden="true" color="green.500" mt={1}/>
                            <Text fontSize={{base: "sm !important", md: "lg !important"}} color={"black !important"}>
                                <strong>Lower energy bills:</strong> Solar panels can significantly reduce your
                                dependence on rising electricity prices.
                            </Text>
                        </HStack>

                        <HStack align="start">
                            <Icon as={FaCheckCircle} aria-hidden="true" color="green.500" mt={1}/>
                            <Text fontSize={{base: "sm !important", md: "lg !important"}} color={"black !important"}>
                                <strong>Clean, renewable power:</strong> Reduce your home’s carbon emissions and make a
                                real environmental impact.
                            </Text>
                        </HStack>

                        <HStack align="start">
                            <Icon as={FaCheckCircle} aria-hidden="true" color="green.500" mt={1}/>
                            <Text fontSize={{base: "sm !important", md: "lg !important"}} color={"black !important"}>
                                <strong>Boost property value:</strong> Solar-equipped homes often receive higher energy
                                performance scores and attract eco-conscious buyers.
                            </Text>
                        </HStack>
                    </VStack>
                </VStack>
            </Box>

            <RegionSelector/>

            <Box
                p={6}
                as={"section"}
                borderRadius="xl"
                boxShadow="md"
                mx="auto"
                textAlign="left"
            >
                <Heading size={{base: "xl", md: "3xl"}} color="green.600" mb={4}>
                    Growing Solar Energy Adoption in Europe
                </Heading>
                <Text fontSize={{base: "md !important", md: "xl !important"}} color={"black !important"} mb={6}>
                    Towards renewable energy sources in Europe reflects a steadfast commitment to sustainability and
                    cost-efficiency.
                    Solar energy, accounting for over 20% of the EU’s gross electricity consumption in 2023, underscores
                    its growing significance in the energy mix.
                    This shift not only reduces reliance on fossil fuels but also offers a pathway to substantial cost
                    savings for consumers.
                    By harnessing the power of the sun, households and businesses can significantly lower their energy
                    bills while contributing to a cleaner, greener future.
                    Implementing solar solutions is more accessible than ever, allowing for an eco-friendly lifestyle
                    that is both economical and sustainable. As solar technology continues to advance, its potential to
                    drive down energy costs will only increase, making it a smart choice for environmentally conscious
                    consumers looking to make a positive impact.
                    Embracing solar energy is not just an investment in your own savings, but also a commitment to
                    preserving the planet for future generations.
                </Text>

                <Image
                    src={`${process.env.PUBLIC_URL}/images/pie.jpg`}
                    borderRadius="md"
                    boxShadow="md"
                    objectFit="cover"
                    width="100%"
                    height="auto"
                />
            </Box>
        </Box>
    );
};

export default Main;